/**
 * FinesView - Traffic Fines Management (ANTAI)
 *
 * Unified Operations View Pattern:
 * - Two-level accordion (current/done groups → status sub-groups)
 * - Card-based items instead of table rows
 * - Same UX as MaintenanceView, InspectionsView, ClaimsView
 *
 * Workflow: received → pending_designation → designated → paid/contested/cancelled
 * SLA: 45 days for driver designation (French legal requirement)
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Plus, Scale, Eye, Trash2, Clock, CheckCircle, XCircle,
  ChevronDown, ChevronRight, FolderOpen, Folder, Archive, Zap,
  Car, UserCheck, FileText, AlertCircle, Euro, Search, RefreshCw
} from 'lucide-react';
import api from '../../api.js';
import { useActiveVehicles, useActiveDrivers } from '../../hooks/useDataHooks.js';
import { useAccordionState } from '../../hooks/useAccordionState.js';
import FineFormModal from './modals/FineFormModal.jsx';
import FineDetailModal from './modals/FineDetailModal.jsx';
import DriverDesignationModal from './modals/DriverDesignationModal.jsx';
import { useSearch } from '../../v3/context/SearchContext.jsx';
import { useOrgCurrency } from '../../hooks/useOrgCurrency.js';
import { useFormat } from '../../i18n/format.js';

// Loading Spinner
function LoadingSpinner() {
  const { t } = useFormat();
  return (
    <div className="flex justify-center items-center h-64">
      <div className="text-gray-600">{t('common.states.loading')}</div>
    </div>
  );
}

// ============================================================================
// STATUS CONFIGURATION (defines accordion grouping)
// ============================================================================
// Les libellés ne sont PAS ici : une constante de module est évaluée une seule fois,
// avant tout changement de langue. Ils se résolvent au rendu via t('fines.status.<code>').
const STATUS_CONFIG = {
  received: { color: 'bg-gray-100 text-gray-800', icon: FileText, group: 'current' },
  pending_designation: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, group: 'current' },
  designated: { color: 'bg-blue-100 text-blue-800', icon: UserCheck, group: 'current' },
  contested: { color: 'bg-purple-100 text-purple-800', icon: AlertCircle, group: 'current' },
  paid: { color: 'bg-green-100 text-green-800', icon: CheckCircle, group: 'done' },
  cancelled: { color: 'bg-gray-100 text-gray-500', icon: XCircle, group: 'done' },
};

// Status order within each group
const STATUS_ORDER = {
  current: ['received', 'pending_designation', 'designated', 'contested'],
  done: ['paid', 'cancelled']
};

// Urgency configuration (for SLA display) — couleurs seules, cf. remarque ci-dessus.
const URGENCY_CONFIG = {
  overdue: { color: 'bg-red-500 text-white' },
  critical: { color: 'bg-red-100 text-red-800' },
  warning: { color: 'bg-orange-100 text-orange-800' },
  normal: { color: 'bg-green-100 text-green-800' },
};

// Argent : un seul outil (utils/formatMoney via useOrgCurrency). La devise vient
// de l'organisation de l'amende, jamais d'un « EUR » codé en dur.
// Libellés de type d'infraction : t('fines.infractionTypesShort.<code>') au rendu.

// ============================================================================
// MAIN COMPONENT
// ============================================================================
function FinesView({ onMessage, autoSelectId, createForVehicleId, returnTo, onNavigate }) {
  const [finesLoading, setFinesLoading] = useState(true);
  const [fines, setFines] = useState([]);
  const { data: vehicles = [], isLoading: vehiclesLoading } = useActiveVehicles();
  const { data: drivers = [], isLoading: driversLoading } = useActiveDrivers();
  const [, setStats] = useState(null);
  const autoSelectProcessed = useRef(false);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDesignationModal, setShowDesignationModal] = useState(false);
  const [selectedFine, setSelectedFine] = useState(null);
  const { searchQuery } = useSearch();
  const { t, fmt } = useFormat();
  // Devise : celle de l'org de l'amende ; total du périmètre converti (jamais sommé brut).
  const { money, sumScope } = useOrgCurrency();
  const fineMoney = (f) => (f?.fine_amount == null || f.fine_amount === '' ? '-' : money(f.fine_amount, f));

  // Accordion state - persisted to DB via useAccordionState hook
  const {
    isExpanded,
    toggleGroup: toggleAccordion,
  } = useAccordionState({ viewCode: 'fines' });

  const loading = finesLoading || vehiclesLoading || driversLoading;

  // Load data
  const loadData = async () => {
    try {
      setFinesLoading(true);
      const [finesRes, statsRes] = await Promise.all([
        api.trafficFine.getAll(),
        api.trafficFine.getStats(),
      ]);
      setFines(finesRes.data || []);
      setStats(statsRes.data || null);
    } catch (error) {
      console.error('Error loading fines:', error);
      onMessage?.({
        title: t('fines.messages.errorTitle'),
        message: t('fines.errors.loadFailed'),
        type: 'error',
      });
    } finally {
      setFinesLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select fine when navigating from another view
  useEffect(() => {
    if (autoSelectId && fines.length > 0 && !autoSelectProcessed.current) {
      const fineToSelect = fines.find(f => f.id === autoSelectId);
      if (fineToSelect) {
        setSelectedFine(fineToSelect);
        setShowDetailModal(true);
        autoSelectProcessed.current = true;
      }
    }
  }, [autoSelectId, fines]);

  useEffect(() => {
    if (autoSelectId) {
      autoSelectProcessed.current = false;
    }
  }, [autoSelectId]);

  // Auto-open creation form for a specific vehicle (from Vehicles action menu)
  const createForVehicleProcessed = useRef(false);
  useEffect(() => {
    if (createForVehicleId && !createForVehicleProcessed.current) {
      createForVehicleProcessed.current = true;
      setShowCreateModal(true);
    }
  }, [createForVehicleId]);

  // Filter fines by search
  const filteredFines = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return fines;
    const query = searchQuery.toLowerCase();
    return fines.filter(fine => {
      const vehicle = vehicles.find(v => v.id === fine.vehicle_id);
      const driver = drivers.find(d => d.id === fine.designated_driver_id);
      return (
        fine.antai_reference?.toLowerCase().includes(query) ||
        fine.infraction_type?.toLowerCase().includes(query) ||
        fine.status?.toLowerCase().includes(query) ||
        vehicle?.license_plate?.toLowerCase().includes(query) ||
        vehicle?.make?.toLowerCase().includes(query) ||
        vehicle?.model?.toLowerCase().includes(query) ||
        driver?.first_name?.toLowerCase().includes(query) ||
        driver?.last_name?.toLowerCase().includes(query)
      );
    });
  }, [fines, vehicles, drivers, searchQuery]);

  // Group fines by current/done and then by status
  const groupedFines = useMemo(() => {
    const groups = {
      current: {
        icon: Zap,
        statuses: {},
        totalCount: 0
      },
      done: {
        icon: Archive,
        statuses: {},
        totalCount: 0
      }
    };

    // Initialize status groups
    STATUS_ORDER.current.forEach(status => {
      groups.current.statuses[status] = [];
    });
    STATUS_ORDER.done.forEach(status => {
      groups.done.statuses[status] = [];
    });

    // Sort fines into groups
    filteredFines.forEach(fine => {
      const statusConfig = STATUS_CONFIG[fine.status];
      if (statusConfig) {
        const groupKey = statusConfig.group;
        groups[groupKey].statuses[fine.status].push(fine);
        groups[groupKey].totalCount++;
      }
    });

    return groups;
  }, [filteredFines]);

  // Helper to compute urgency from days_remaining
  const computeUrgency = (fine) => {
    // Only relevant for active fines that need designation
    if (!['received', 'pending_designation'].includes(fine.status)) return 'normal';

    const daysRemaining = fine.days_remaining ?? fine.days_until_deadline;
    if (daysRemaining === null || daysRemaining === undefined) return 'normal';

    if (daysRemaining <= 0) return 'overdue';
    if (daysRemaining <= 3) return 'critical';
    if (daysRemaining <= 10) return 'warning';
    return 'normal';
  };

  // Computed stats - only count urgent for current (active) fines
  const computedStats = useMemo(() => {
    const currentFines = fines.filter(f => STATUS_CONFIG[f.status]?.group === 'current');

    const urgentCount = currentFines.filter(f => {
      const urgency = f.urgency || computeUrgency(f);
      return urgency === 'critical' || urgency === 'overdue';
    }).length;

    const overdueCount = currentFines.filter(f => {
      const urgency = f.urgency || computeUrgency(f);
      return urgency === 'overdue';
    }).length;

    return {
      total: fines.length,
      current: groupedFines.current.totalCount,
      done: groupedFines.done.totalCount,
      pending: fines.filter(f => f.status === 'pending_designation').length,
      urgent: urgentCount,
      overdue: overdueCount,
      // Somme multi-devises : convertie en devise du périmètre, jamais additionnée brute.
      totalAmount: sumScope(fines, f => f.fine_amount)
    };
  }, [fines, groupedFines, sumScope]);

  // Helper functions
  const getVehicle = (vehicleId) => vehicles.find(v => v.id === vehicleId);
  const getDriver = (driverId) => drivers.find(d => d.id === driverId);

  // Reserved for future use in card display
  // const getVehicleInfo = (vehicleId) => {
  //   const vehicle = getVehicle(vehicleId);
  //   return vehicle ? `${vehicle.license_plate} - ${vehicle.make} ${vehicle.model}` : '-';
  // };

  // const getDriverInfo = (driverId) => {
  //   if (!driverId) return null;
  //   const driver = getDriver(driverId);
  //   return driver ? `${driver.first_name} ${driver.last_name}` : '-';
  // };

  // Toggle functions - use prefixed keys for DB persistence
  const toggleGroup = (group) => toggleAccordion(`group:${group}`);
  const toggleStatus = (status) => toggleAccordion(`status:${status}`);

  // Action handlers
  const handleViewFine = (fine) => {
    setSelectedFine(fine);
    setShowDetailModal(true);
  };

  const handleDesignateFine = (fine) => {
    setSelectedFine(fine);
    setShowDesignationModal(true);
  };

  const handleDeleteFine = async (fineId) => {
    if (!window.confirm(t('fines.messages.confirmDelete'))) return;

    try {
      await api.trafficFine.delete(fineId);
      loadData();
      onMessage?.({
        title: t('fines.messages.successTitle'),
        message: t('fines.messages.deleted'),
        type: 'success',
      });
    } catch (error) {
      onMessage?.({
        title: t('fines.messages.errorTitle'),
        message: error.message || t('fines.errors.deleteFailed'),
        type: 'error',
      });
    }
  };

  const handleWorkflowAction = async (fineId, action, data) => {
    try {
      await api.trafficFine[action](fineId, data);
      loadData();
      onMessage?.({
        title: t('fines.messages.successTitle'),
        message: t('fines.messages.actionDone'),
        type: 'success',
      });
    } catch (error) {
      onMessage?.({
        title: t('fines.messages.errorTitle'),
        message: error.message || t('fines.errors.actionFailed'),
        type: 'error',
      });
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      {/* Form Modal */}
      {showCreateModal && (
        <FineFormModal
          fine={selectedFine}
          vehicles={vehicles}
          drivers={drivers}
          defaultVehicleId={createForVehicleId}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedFine(null);
          }}
          onSuccess={() => {
            setShowCreateModal(false);
            setSelectedFine(null);
            loadData();
            onMessage?.({
              title: t('fines.messages.successTitle'),
              message: selectedFine ? t('fines.messages.updated') : t('fines.messages.created'),
              type: 'success',
            });
            // Return to origin view (e.g. vehicles) after successful submission
            if (returnTo && onNavigate) {
              onNavigate(returnTo);
            }
          }}
          onMessage={onMessage}
        />
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedFine && (
        <FineDetailModal
          fine={selectedFine}
          vehicles={vehicles}
          drivers={drivers}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedFine(null);
          }}
          onAction={handleWorkflowAction}
          onDesignate={() => {
            setShowDetailModal(false);
            setShowDesignationModal(true);
          }}
          onMessage={onMessage}
        />
      )}

      {/* Designation Modal */}
      {showDesignationModal && selectedFine && (
        <DriverDesignationModal
          fine={selectedFine}
          vehicle={getVehicle(selectedFine.vehicle_id)}
          drivers={drivers}
          onClose={() => {
            setShowDesignationModal(false);
            setSelectedFine(null);
          }}
          onDesignate={async (fineId, driverId) => {
            // Call the API to designate driver
            const result = await api.trafficFine.designate(fineId, driverId);
            if (!result.ok) {
              throw new Error(result.error?.message || t('fines.errors.designationFailed'));
            }
            // Reload data after successful designation
            loadData();
            onMessage?.({
              title: t('fines.messages.successTitle'),
              message: t('fines.messages.designated'),
              type: 'success',
            });
          }}
          onMessage={onMessage}
        />
      )}

      {/* Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Scale className="w-6 h-6 text-blue-600" />
              {t('fines.list.title')}
            </h2>
            <p className="text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900">{computedStats.total}</span> {t('fines.list.fineNoun', { count: computedStats.total })}
              <span className="text-gray-300">|</span>
              <span className="text-yellow-600">{t('fines.list.pending', { count: computedStats.pending })}</span>
              {computedStats.urgent > 0 && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="text-orange-600">{t('fines.list.urgent', { count: computedStats.urgent })}</span>
                </>
              )}
              {computedStats.overdue > 0 && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="text-red-600">{t('fines.list.overdue', { count: computedStats.overdue })}</span>
                </>
              )}
              <span className="text-gray-300">|</span>
              <span className="text-green-600">{t('fines.list.paid', { count: groupedFines.done.statuses.paid?.length || 0 })}</span>
              <span className="text-gray-300">|</span>
              <span className="text-blue-600">{computedStats.totalAmount.label}</span>
            </p>
            <p className="text-xs text-gray-400 mt-1">{t('fines.list.slaHint')}</p>
          </div>
          <button
            onClick={() => {
              setSelectedFine(null);
              setShowCreateModal(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {t('fines.list.newFine')}
          </button>
        </div>

        {/* Refresh */}
        <div className="flex gap-4 pt-4 border-t border-gray-200 justify-end">
          <button
            onClick={loadData}
            disabled={loading}
            className="px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg border border-gray-300 transition-colors"
            title={t('common.actions.refresh')}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Accordion Groups */}
      <div className="space-y-3">
        {['current', 'done'].map((groupKey) => {
          const group = groupedFines[groupKey];
          const isGroupExpanded = isExpanded(`group:${groupKey}`);
          const GroupIcon = group.icon;
          const statusList = STATUS_ORDER[groupKey];
          const nonEmptyStatuses = statusList.filter(s => group.statuses[s].length > 0);

          return (
            <div
              key={groupKey}
              className={`border rounded-lg overflow-hidden transition-all ${
                isGroupExpanded ? 'border-blue-300 shadow-sm' : 'border-gray-200'
              }`}
            >
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(groupKey)}
                className={`w-full flex items-center justify-between p-4 text-left transition-colors ${
                  isGroupExpanded
                    ? 'bg-blue-50 hover:bg-blue-100'
                    : 'bg-white hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  {isGroupExpanded ? (
                    <FolderOpen className="w-5 h-5 text-blue-600" />
                  ) : (
                    <Folder className="w-5 h-5 text-gray-400" />
                  )}
                  <GroupIcon className={`w-5 h-5 ${
                    groupKey === 'current' ? 'text-orange-500' : 'text-green-500'
                  }`} />
                  <span className={`text-lg font-semibold ${
                    isGroupExpanded ? 'text-blue-900' : 'text-gray-900'
                  }`}>
                    {t(`fines.groups.${groupKey}`)}
                  </span>
                  {groupKey === 'current' && computedStats.urgent > 0 && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full animate-pulse">
                      {t('fines.list.urgent', { count: computedStats.urgent })}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-4">
                  {!isGroupExpanded && (
                    <span className="text-sm text-gray-500">
                      {t('fines.list.groupSummary', {
                        fines: t('fines.list.fineCountLabel', { count: group.totalCount }),
                        statuses: t('fines.list.statusCountLabel', { count: nonEmptyStatuses.length }),
                      })}
                    </span>
                  )}
                  <span className={`px-2.5 py-1 text-sm font-medium rounded-full ${
                    isGroupExpanded ? 'bg-blue-200 text-blue-800' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {group.totalCount}
                  </span>
                  {isGroupExpanded ? (
                    <ChevronDown className="w-5 h-5 text-blue-600" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>

              {/* Group Content - Status Accordions */}
              {isGroupExpanded && (
                <div className="border-t border-blue-200 bg-gray-50">
                  {/* Status List */}
                  <div className="divide-y divide-gray-200">
                    {statusList.map((status) => {
                      const statusFines = group.statuses[status];
                      const statusConfig = STATUS_CONFIG[status];
                      const StatusIcon = statusConfig.icon;
                      const isStatusExpanded = isExpanded(`status:${status}`);

                      if (statusFines.length === 0) return null;

                      return (
                        <div key={status} className="bg-white">
                          {/* Status Header */}
                          <button
                            onClick={() => toggleStatus(status)}
                            className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                              isStatusExpanded
                                ? 'bg-gray-50 border-l-4 border-blue-500'
                                : 'hover:bg-gray-50 border-l-4 border-transparent'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {isStatusExpanded ? (
                                <ChevronDown className="w-4 h-4 text-blue-600" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              )}
                              <StatusIcon className={`w-4 h-4 ${
                                statusConfig.color.includes('gray') ? 'text-gray-500' :
                                statusConfig.color.includes('blue') ? 'text-blue-500' :
                                statusConfig.color.includes('yellow') ? 'text-yellow-500' :
                                statusConfig.color.includes('green') ? 'text-green-500' :
                                statusConfig.color.includes('purple') ? 'text-purple-500' :
                                statusConfig.color.includes('red') ? 'text-red-500' :
                                'text-gray-500'
                              }`} />
                              <span className={`font-medium ${
                                isStatusExpanded ? 'text-blue-900' : 'text-gray-700'
                              }`}>
                                {t(`fines.status.${status}`)}
                              </span>
                            </div>

                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig.color}`}>
                              {t('fines.list.fineCountLabel', { count: statusFines.length })}
                            </span>
                          </button>

                          {/* Status Content - Fine Cards */}
                          {isStatusExpanded && (
                            <div className="px-4 py-3 space-y-2 bg-gray-50 border-l-4 border-blue-500">
                              {statusFines.map(fine => {
                                const vehicle = getVehicle(fine.vehicle_id);
                                const designatedDriver = getDriver(fine.designated_driver_id);
                                const urgency = URGENCY_CONFIG[fine.urgency] || URGENCY_CONFIG.normal;

                                return (
                                  <div
                                    key={fine.id}
                                    className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1 min-w-0">
                                        {/* Title row with reference and urgency */}
                                        <div className="flex items-center gap-2 mb-1">
                                          <Scale className="w-4 h-4 text-gray-400" />
                                          <h4 className="font-medium text-gray-900">
                                            {fine.antai_reference || `AME-${fine.id}`}
                                          </h4>
                                          {fine.status === 'pending_designation' && fine.days_remaining !== null && (
                                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${urgency.color}`}>
                                              {fine.days_remaining <= 0
                                                ? t('fines.list.daysOverdue', { count: Math.abs(fine.days_remaining) })
                                                : t('fines.list.daysRemaining', { count: fine.days_remaining })}
                                            </span>
                                          )}
                                        </div>

                                        {/* Vehicle info */}
                                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                                          <Car className="w-4 h-4 text-gray-400" />
                                          <span>{vehicle?.license_plate || '-'}</span>
                                          <span className="text-gray-400">•</span>
                                          <span>{vehicle?.make} {vehicle?.model}</span>
                                        </div>

                                        {/* Details row */}
                                        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                                          <span className="flex items-center gap-1">
                                            <FileText className="w-3 h-3" />
                                            {t(`fines.infractionTypesShort.${fine.infraction_type}`, { defaultValue: fine.infraction_type })}
                                          </span>
                                          <span>
                                            {fmt.date(fine.infraction_date)}
                                          </span>
                                          <span className="font-medium text-gray-700">
                                            <Euro className="w-3 h-3 inline mr-1" />
                                            {fineMoney(fine)}
                                          </span>
                                          {fine.points_deducted > 0 && (
                                            <span className="text-red-600">{t('fines.list.pointsShort', { count: fine.points_deducted })}</span>
                                          )}
                                          {designatedDriver && (
                                            <span className="flex items-center gap-1 text-green-600">
                                              <UserCheck className="w-3 h-3" />
                                              {designatedDriver.first_name} {designatedDriver.last_name}
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                      {/* Actions */}
                                      <div className="flex items-center gap-2 ml-4">
                                        {fine.status === 'pending_designation' && (
                                          <button
                                            onClick={() => handleDesignateFine(fine)}
                                            className="px-3 py-1.5 text-sm bg-orange-100 text-orange-700 hover:bg-orange-200 rounded-lg flex items-center gap-1"
                                            title={t('fines.list.designateTitle')}
                                          >
                                            <UserCheck className="w-4 h-4" />
                                            {t('fines.list.designate')}
                                          </button>
                                        )}
                                        <button
                                          onClick={() => handleViewFine(fine)}
                                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                          title={t('fines.list.viewDetails')}
                                        >
                                          <Eye className="w-4 h-4" />
                                        </button>
                                        {['received', 'pending_designation'].includes(fine.status) && (
                                          <button
                                            onClick={() => handleDeleteFine(fine.id)}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                            title={t('common.actions.delete')}
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Empty state for group */}
                    {nonEmptyStatuses.length === 0 && (
                      <div className="py-8 text-center text-gray-500">
                        <Scale className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>{groupKey === 'current' ? t('fines.list.emptyCurrent') : t('fines.list.emptyDone')}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Global Empty State */}
      {fines.length === 0 && (
        <div className="bg-white p-12 rounded-lg shadow-md text-center">
          <Scale className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">{t('fines.empty.title')}</h3>
          <p className="text-gray-600 mb-4">
            {t('fines.empty.description')}
          </p>
          <button
            onClick={() => {
              setSelectedFine(null);
              setShowCreateModal(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {t('fines.empty.action')}
          </button>
        </div>
      )}
    </div>
  );
}

export default FinesView;
